/*
 * The screens the inbox never draws (the campaigns list, a campaign, standing answers and settings)
 * are downloaded when first opened, not with the inbox (frontend performance plan, W6; Dawid,
 * 2026-10-19, "Build it"), so the inbox's first load carries less.
 *
 * A deploy replaces every file under new names. A tab opened before it still knows the old names, so
 * the first screen it opens afterwards asks for a file the server no longer has, and is answered with
 * the page itself or a 404. That screen then reloads the page once, which asks for the new names. A
 * second failure within `reloadWindowMs` says so instead of reloading again, so this cannot loop, and
 * storage that cannot be read or written never reloads at all. `boot-recovery.ts` does the same for
 * the app's own bundle; this covers what that bundle loads later.
 */
import * as Sentry from "@sentry/react";
import { Component, type ComponentType, lazy, type ReactNode, Suspense } from "react";
import { LoadFailedPage } from "./shell/app-shell.tsx";

export interface ScreenRecoveryConfig {
  readonly reloadKey: string;
  /** A reload inside this window of the last one is not repeated. */
  readonly reloadWindowMs: number;
}

export const SCREEN_RECOVERY: ScreenRecoveryConfig = {
  reloadKey: "leadcue-reply:screen-reload",
  reloadWindowMs: 60_000,
};

export interface ScreenRecoveryPorts {
  now(): number;
  /** Either may throw: storage can be unavailable, and then nothing reloads. */
  readonly storage: { get(key: string): string | null; set(key: string, value: string): void };
  reload(): void;
}

/** Reloads the page for a screen that did not load, unless it already did so within the window. */
export function reloadForScreen(ports: ScreenRecoveryPorts, config = SCREEN_RECOVERY): boolean {
  try {
    const last = Number(ports.storage.get(config.reloadKey) ?? Number.NaN);
    if (Number.isFinite(last) && ports.now() - last < config.reloadWindowMs) return false;
    ports.storage.set(config.reloadKey, String(ports.now()));
  } catch {
    return false;
  }
  ports.reload();
  return true;
}

const browserPorts = (): ScreenRecoveryPorts => ({
  now: () => Date.now(),
  storage: {
    get: (key) => sessionStorage.getItem(key),
    set: (key, value) => sessionStorage.setItem(key, value),
  },
  reload: () => location.reload(),
});

/** A screen that could not load and will not be reloaded for: said, with a reload to try again. */
class ScreenBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    Sentry.captureException(error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="lcr-settings">
        <LoadFailedPage what="this page" onRetry={() => location.reload()} />
      </main>
    );
  }
}

/**
 * `load` resolves to the screen's component, as `() => import("./x.tsx").then((m) => m.X)`. While it
 * downloads nothing is drawn in its place; while the page reloads for it, nothing either.
 */
export function lazyScreen<P extends object>(
  load: () => Promise<ComponentType<P>>,
  ports: () => ScreenRecoveryPorts = browserPorts,
): ComponentType<P> {
  const Screen = lazy(() =>
    load().then(
      (component) => ({ default: component }),
      (error: unknown) =>
        reloadForScreen(ports()) ? new Promise<never>(() => undefined) : Promise.reject(error),
    ),
  );
  return function LazyScreen(props: P) {
    return (
      <ScreenBoundary>
        <Suspense fallback={null}>
          <Screen {...props} />
        </Suspense>
      </ScreenBoundary>
    );
  };
}
